import { useState, useCallback, useEffect, useRef } from 'react'
import { signIn, signUp, confirmSignUp, resetPassword, confirmResetPassword, resendSignUpCode, signOut, confirmSignIn, getCurrentUser, fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth'
import { secureLogger, type AuthEvent } from '../lib/secure-logger'
import { useAmplify } from '../contexts/amplify-context'
import { isAmplifyReady } from '../lib/amplify'


export type AuthState =
  | 'signIn'
  | 'signUp'
  | 'confirmSignUp'
  | 'forgotPassword'
  | 'confirmResetPassword'
  | 'confirmSignInWithNewPassword'
  | 'authenticated'

export interface AuthError {
  code?: string
  message: string
}

export interface CustomAuthState {
  currentState: AuthState
  isLoading: boolean
  error: AuthError | null
  user: unknown | null
  tempEmail?: string // Store email for verification flow
  fromLogin?: boolean // Track if user came from login (vs signup)
}

export interface AuthFormData {
  email: string
  password: string
  confirmPassword?: string
  name?: string
  phoneNumber?: string
  code?: string
  // Dynamic attributes - any additional Cognito attributes
  [key: string]: string | undefined
}

export interface SignInResult {
  success: boolean
  error?: { code?: string; message: string }
  needsNewPassword?: boolean
  needsConfirmation?: boolean
}

export function useCustomAuth() {
  const [authState, setAuthState] = useState<CustomAuthState>({
    currentState: 'signIn',
    isLoading: true, // Start with loading while checking existing auth
    error: null,
    user: null
  })
  const { isConfigured: isAmplifyConfigured } = useAmplify()
  const hasCheckedAuth = useRef(false)


  const setLoading = useCallback((loading: boolean) => {
    setAuthState(prev => ({ ...prev, isLoading: loading }))
  }, [])

  const setError = useCallback((error: AuthError | null) => {
    setAuthState(prev => ({ ...prev, error }))
  }, [])

  const changeState = useCallback((newState: AuthState, tempEmail?: string, fromLogin?: boolean) => {
    setAuthState(prev => ({
      ...prev,
      currentState: newState,
      error: null,
      tempEmail: tempEmail || prev.tempEmail,
      fromLogin: fromLogin !== undefined ? fromLogin : prev.fromLogin
    }))
  }, [authState.currentState])

  // Check for existing authentication on hook initialization
  useEffect(() => {
    if (!isAmplifyConfigured) {
      console.log('⏳ [useCustomAuth] Waiting for Amplify to be configured...', {
        isAmplifyConfigured,
        currentState: authState.currentState
      })
      return
    }

    // Prevent duplicate checks from React Strict Mode double-mounting
    if (hasCheckedAuth.current) {
      console.log('🔄 [useCustomAuth] Auth check already in progress, skipping duplicate')
      return
    }

    console.log('✅ [useCustomAuth] Amplify is configured, proceeding with auth check', {
      currentState: authState.currentState
    })

    const checkExistingAuth = async () => {
      // Wait for Amplify singleton to actually be configured
      // React context state can propagate before Amplify.configure() completes
      let retries = 0
      const maxRetries = 10
      while (!isAmplifyReady() && retries < maxRetries) {
        console.log(`⏳ [useCustomAuth] Waiting for Amplify singleton... attempt ${retries + 1}/${maxRetries}`)
        await new Promise(resolve => setTimeout(resolve, 100))
        retries++
      }

      if (!isAmplifyReady()) {
        console.error('❌ [useCustomAuth] Amplify singleton never configured after max retries')
        setAuthState(prev => ({
          ...prev,
          currentState: 'signIn',
          isLoading: false,
          error: { code: 'ConfigurationError', message: 'Failed to configure authentication. Please refresh the page.' }
        }))
        return
      }

      hasCheckedAuth.current = true
      console.log('✅ [useCustomAuth] Amplify singleton confirmed configured')

      try {
        // Check if we're returning from a SAML redirect
        const urlParams = new URLSearchParams(window.location.search)
        const hasAuthCode = urlParams.has('code')
        const hasState = urlParams.has('state')
        
        if (hasAuthCode) {
          // Let Amplify handle the OAuth/SAML callback with retry logic
          // Amplify needs time to exchange the code for tokens
          const maxRetries = 5
          let retryCount = 0
          
          while (retryCount < maxRetries) {
            try {
              // Wait before retry (except first attempt)
              if (retryCount > 0) {
                await new Promise(resolve => setTimeout(resolve, 500 * retryCount))
              }
              
              // Try to fetch the session - this triggers the OAuth code exchange
              const session = await fetchAuthSession({ forceRefresh: true })
              
              if (session.tokens) {
                // Session has tokens, now try to get the user
                const currentUser = await getCurrentUser()
                const userAttributes = await fetchUserAttributes()

                // Create and store user profile
                const userEmail = currentUser.signInDetails?.loginId || currentUser.username || 'unknown@example.com'
                
                localStorage.removeItem('user-profile')
                
                const userProfile = {
                  id: currentUser.userId || currentUser.username,
                  email: userEmail,
                  name: userAttributes.name || currentUser.username,
                  role: 'employee' as const,
                  avatar: userAttributes.picture || undefined,
                  department: userAttributes.department || undefined,
                  position: userAttributes.position || undefined,
                  status: 'active' as const
                }

                localStorage.setItem('user-profile', JSON.stringify(userProfile))

                setAuthState(prev => ({
                  ...prev,
                  currentState: 'authenticated',
                  user: currentUser,
                  isLoading: false
                }))
                
                // Clean up URL parameters
                window.history.replaceState({}, document.title, window.location.pathname)
                
                // Small delay before redirect
                await new Promise(resolve => setTimeout(resolve, 100))

                // Redirect to dashboard
                window.location.href = '/dashboard'
                return
              }
              
              // No tokens yet, retry
              retryCount++
              
            } catch (oauthError: any) {
              retryCount++
              console.error('❌ OAuth authentication failed after retries:', oauthError)
              // If this is the last retry or a non-auth error, fail
              if (retryCount >= maxRetries) {
                console.error('❌ OAuth authentication failed after retries:', oauthError)
                
                const errorMessage = oauthError?.message?.includes('oauth param not configured')
                  ? 'OAuth SSO não está configurado corretamente no AWS Cognito.'
                  : 'Falha na autenticação OAuth. Tente novamente.'
                
                setAuthState(prev => ({
                  ...prev,
                  currentState: 'signIn',
                  isLoading: false,
                  error: {
                    code: 'OAuthAuthenticationFailed',
                    message: errorMessage
                  }
                }))
                
                // Clean up URL to allow retry
                window.history.replaceState({}, document.title, window.location.pathname)
                return
              }
              
              // Otherwise, keep retrying
              continue
            }
          }
        }
        
        // Regular authentication check
        const currentUser = await getCurrentUser()

        if (currentUser) {
          // Set AWS auth state first
          setAuthState(prev => ({
            ...prev,
            currentState: 'authenticated',
            user: currentUser,
            isLoading: false
          }))
          
          // Note: Don't sync with local auth here - let AuthContainer handle it
          // This avoids circular dependencies and keeps responsibilities clear

        } else {
          setAuthState(prev => ({
            ...prev,
            currentState: 'signIn',
            isLoading: false
          }))
        }
      } catch (error) {
        console.error('❌ Error checking existing auth:', error)
        // This is expected when user is not authenticated
        // Only reset to signIn if we're not in an active auth flow
        setAuthState(prev => ({
          ...prev,
          currentState: prev.currentState === 'authenticated' ? 'signIn' : prev.currentState,
          isLoading: false
        }))
      }
    }

    // Don't check existing auth if we're in the middle of an auth flow
    const isInActiveAuthFlow = [
      'confirmSignInWithNewPassword',
      'confirmSignUp',
      'confirmResetPassword'
    ].includes(authState.currentState)

    if (!isInActiveAuthFlow) {
      checkExistingAuth()
    }
  }, [isAmplifyConfigured, authState.currentState])

  const handleSignIn = useCallback(async (email: string, password: string, isRetry = false): Promise<SignInResult> => {
    secureLogger.authEvent('USER_LOGIN_ATTEMPT' as AuthEvent, { email, isRetry })
    setLoading(true)
    setError(null)

    // Verify Amplify is actually configured before attempting sign in
    if (!isAmplifyReady()) {
      console.error('❌ [handleSignIn] Amplify not configured yet')
      const authError: AuthError = {
        code: 'ConfigurationError',
        message: 'Sistema ainda carregando. Aguarde um momento e tente novamente.'
      }
      setError(authError)
      setLoading(false)
      return { success: false, error: authError }
    }

    try {
      secureLogger.debug('Calling AWS Cognito signIn')

      // Real AWS Amplify signIn
      const result = await signIn({ username: email, password })

      secureLogger.authEvent('USER_LOGIN_SUCCESS' as AuthEvent)

      // Handle different next steps based on the result
      if (result.isSignedIn) {
        // User is fully authenticated
        secureLogger.debug('User is fully signed in')
        setAuthState(prev => ({
          ...prev,
          currentState: 'authenticated',
          user: result,
          tempEmail: email,
          isLoading: false
        }))
        return { success: true }
      } else if (result.nextStep) {
        // Handle different next steps
        const { signInStep } = result.nextStep

        secureLogger.debug('SignIn nextStep detected:', { signInStep, allNextStepData: result.nextStep })
        console.log('🔍 DEBUG: SignIn nextStep =', signInStep)
        console.log('🔍 DEBUG: Full result.nextStep =', JSON.stringify(result.nextStep, null, 2))

        switch (signInStep) {
          case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
            secureLogger.info('New password required')
            console.log('✅ NEW_PASSWORD_REQUIRED challenge detected - showing NewPasswordForm')
            setAuthState(prev => ({
              ...prev,
              currentState: 'confirmSignInWithNewPassword',
              user: result,
              tempEmail: email,
              isLoading: false
            }))
            return { success: true, needsNewPassword: true }

          case 'CONFIRM_SIGN_UP':
            setAuthState(prev => ({
              ...prev,
              currentState: 'confirmSignUp',
              user: result,
              tempEmail: email,
              fromLogin: true,
              isLoading: false
            }))
            return { success: true, needsConfirmation: true }
            
          default:
            console.warn('⚠️ Unhandled signIn step:', signInStep)
            setAuthState(prev => ({
              ...prev,
              currentState: 'authenticated',
              user: result,
              tempEmail: email,
              isLoading: false
            }))
            return { success: true }
        }
      }

      // Fallback - shouldn't happen but just in case
      setAuthState(prev => ({
        ...prev,
        currentState: 'authenticated',
        user: result,
        tempEmail: email,
        isLoading: false
      }))

      return { success: true }

    } catch (error: unknown) {
      console.error('❌ SignIn error:', error)
      const authError: AuthError = {
        code: (error as { name?: string }).name || 'UnknownError',
        message: (error as { message?: string }).message || 'Erro desconhecido ao fazer login'
      }

      setError(authError)
      setLoading(false)
      return { success: false, error: authError }
    }
  }, [setLoading, setError])

  const handleSignUp = useCallback(async (formData: AuthFormData) => {
    setLoading(true)
    setError(null)

    try {
      // Real AWS Amplify signUp
      const result = await signUp({
        username: formData.email,
        password: formData.password!,
        options: {
          userAttributes: {
            email: formData.email,
            name: formData.name,
            phone_number: formData.phoneNumber
          }
        }
      })

      setAuthState(prev => ({
        ...prev,
        currentState: 'confirmSignUp',
        tempEmail: formData.email,
        fromLogin: false,
        isLoading: false
      }))

      return { success: true, needsConfirmation: true }

    } catch (error: unknown) {
      console.error('❌ SignUp error:', error)
      const authError: AuthError = {
        code: (error as { name?: string }).name || 'UnknownError',
        message: (error as { message?: string }).message || 'Erro desconhecido ao criar conta'
      }

      setError(authError)
      setLoading(false)
      return { success: false, error: authError }
    }
  }, [setLoading, setError])

  const handleConfirmSignUp = useCallback(async (email: string, code: string) => {
    setLoading(true)
    setError(null)

    try {
      // Real AWS Amplify confirmSignUp
      const result = await confirmSignUp({
        username: email,
        confirmationCode: code
      })

      // After successful confirmation, fetch the actual user object
      // User is now authenticated, so we can get their full profile
      const currentUser = await getCurrentUser()

      setAuthState(prev => ({
        ...prev,
        currentState: 'authenticated',
        user: currentUser,
        isLoading: false
      }))

      return { success: true }

    } catch (error: unknown) {
      console.error('❌ ConfirmSignUp error:', error)
      const authError: AuthError = {
        code: (error as { name?: string }).name || 'UnknownError',
        message: (error as { message?: string }).message || 'Erro desconhecido ao verificar código'
      }

      setError(authError)
      setLoading(false)
      return { success: false, error: authError }
    }
  }, [setLoading, setError])

  const handleResendCode = useCallback(async (email: string) => {
    setLoading(true)
    setError(null)

    try {
      // Real AWS Amplify resendSignUpCode
      await resendSignUpCode({ username: email })

      setLoading(false)
      return { success: true }

    } catch (error: unknown) {
      console.error('❌ ResendCode error:', error)
      const authError: AuthError = {
        code: (error as { name?: string }).name || 'UnknownError',
        message: (error as { message?: string }).message || 'Erro desconhecido ao reenviar código'
      }

      setError(authError)
      setLoading(false)
      return { success: false, error: authError }
    }
  }, [setLoading, setError])

  const handleForgotPassword = useCallback(async (email: string) => {
    if (!email || !email.includes('@')) {
      const authError: AuthError = {
        code: 'InvalidParameterException',
        message: 'Por favor, insira um email válido'
      }
      setError(authError)
      return { success: false, error: authError }
    }

    setLoading(true)
    setError(null)

    try {
      // Real AWS Amplify resetPassword
      const result = await resetPassword({ username: email })

      setAuthState(prev => ({
        ...prev,
        currentState: 'confirmResetPassword',
        tempEmail: email,
        isLoading: false
      }))

      return { success: true }

    } catch (error: unknown) {
      console.error('❌ ForgotPassword error:', error)
      const errorObj = error as { name?: string; message?: string; code?: string; stack?: string };
      console.error('❌ Error details:', {
        name: errorObj.name,
        message: errorObj.message,
        code: errorObj.code,
        stack: errorObj.stack
      })
      
      let errorMessage = 'Erro desconhecido ao solicitar recuperação de senha'
      
      // Handle common AWS Cognito errors
      switch (errorObj.name) {
        case 'UserNotFoundException':
          errorMessage = 'Usuário não encontrado. Verifique se o email está correto.'
          break
        case 'InvalidParameterException':
          errorMessage = 'Email inválido. Verifique se o formato está correto.'
          break
        case 'LimitExceededException':
          errorMessage = 'Muitas tentativas. Tente novamente mais tarde.'
          break
        case 'NotAuthorizedException':
          errorMessage = 'Email não autorizado para recuperação de senha.'
          break
        default:
          errorMessage = errorObj.message || 'Erro ao enviar código de recuperação'
      }

      const authError: AuthError = {
        code: errorObj.name || 'UnknownError',
        message: errorMessage
      }

      setError(authError)
      setLoading(false)
      return { success: false, error: authError }
    }
  }, [setLoading, setError])

  const handleResetPassword = useCallback(async (email: string, code: string, newPassword: string) => {
    setLoading(true)
    setError(null)

    try {
      // Real AWS Amplify confirmResetPassword
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword: newPassword
      })

      setAuthState(prev => ({
        ...prev,
        currentState: 'signIn',
        tempEmail: undefined,
        isLoading: false
      }))

      return { success: true }

    } catch (error: unknown) {
      console.error('❌ ResetPassword error:', error)
      const authError: AuthError = {
        code: (error as { name?: string }).name || 'UnknownError',
        message: (error as { message?: string }).message || 'Erro desconhecido ao redefinir senha'
      }

      setError(authError)
      setLoading(false)
      return { success: false, error: authError }
    }
  }, [setLoading, setError])

  const handleConfirmSignInWithNewPassword = useCallback(async (newPassword: string) => {
    setLoading(true)
    setError(null)

    try {
      // Real AWS Amplify confirmSignIn with new password
      const result = await confirmSignIn({ challengeResponse: newPassword })

      if (result.isSignedIn) {
        setAuthState(prev => ({
          ...prev,
          currentState: 'authenticated',
          user: result,
          isLoading: false
        }))
        return { success: true }
      } else {
        // Handle any additional next steps if needed
        console.warn('⚠️ New password set but user not fully signed in:', result)
        setAuthState(prev => ({
          ...prev,
          currentState: 'signIn',
          user: null,
          isLoading: false
        }))
        return { success: false, error: { message: 'Senha atualizada mas autenticação incompleta. Tente fazer login novamente.' } }
      }

    } catch (error: unknown) {
      console.error('❌ ConfirmSignIn with new password error:', error)
      const authError: AuthError = {
        code: (error as { name?: string }).name || 'UnknownError',
        message: (error as { message?: string }).message || 'Erro desconhecido ao definir nova senha'
      }

      setError(authError)
      setLoading(false)
      return { success: false, error: authError }
    }
  }, [setLoading, setError])

  const handleSignOut = useCallback(async () => {
    setLoading(true)

    try {
      // Real AWS Amplify signOut
      await signOut()

      setAuthState({
        currentState: 'signIn',
        isLoading: false,
        error: null,
        user: null
      })

    } catch (error: unknown) {
      console.error('❌ SignOut error:', error)
      setLoading(false)
    }
  }, [setLoading])

  return {
    authState,
    changeState,
    handleSignIn,
    handleSignUp,
    handleConfirmSignUp,
    handleResendCode,
    handleForgotPassword,
    handleResetPassword,
    handleConfirmSignInWithNewPassword,
    handleSignOut
  }
}